<template>
  <div v-if="newsletterTranslation" class="o-component o-component--newsletter">
    <div class="m-newsletter">
      <div class="o-container a-layout">
        <div class="u-col-span:6@md">
          <div class="m-newsletter__info">
            <h2 class="m-newsletter__title">{{ newsletterTranslation.title }}</h2>
            <div class="m-newsletter__info o-editor-content o-editor-content--newsletter">
              <p>{{ newsletterTranslation.description }}</p>
            </div>
          </div>
        </div>
        <div class="u-col-span:6@md">
          <div class="m-newsletter__form-alert" role="alert">
            <span v-if="subscribedToNewsletter && errorMessage === ''" class="m-newsletter__alert--good">{{
              translate('newsletter.message.success') }}</span>
            <span v-if="!subscribedToNewsletter && errorMessage !== ''" class="m-newsletter__alert--error">{{ errorMessage
            }}</span>
          </div>
          <form ref="form" method="post" :action="props.endpoint" class="m-newsletter__form" @submit="submitForm"
            v-if="!subscribedToNewsletter" :aria-busy="loading">
            <label :for="`${formId}-email`" class="u-screen-reader-only">{{ newsletterTranslation.label }}</label>
            <input class="m-newsletter__input" type="email" :placeholder="newsletterTranslation.placeholder"
              :id="`${formId}-email`" name="email" autocomplete="email" maxlength="255" required />
            <div class="m-newsletter__website" aria-hidden="true">
              <label :for="`${formId}-website`">Website</label>
              <input :id="`${formId}-website`" type="text" name="website" autocomplete="off" tabindex="-1">
            </div>
            <altcha-widget ref="captcha" challenge="/form/challenge" :language="language"
              display="invisible" auto="off" workers="2" configuration='{"humanInteractionSignature":false}'></altcha-widget>
            <button class="m-newsletter__btn" type="submit" :disabled="loading">
              {{ loading ? translate('newsletter.message.sending') : newsletterTranslation.button_text }}
            </button>
          </form>
          <div class="m-newsletter__disclaimer o-editor-content o-editor-content--disclaimer"
            v-if="!subscribedToNewsletter">
            <p> {{ newsletterTranslation.disclaimer }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Ref, ref, onMounted, useId } from 'vue'
import { translate } from '../helper-functions'
import { useCookies } from 'vue3-cookies'
import 'altcha'
import 'altcha/i18n/nl'
import 'altcha/i18n/de'

interface NewsletterTranslation {
  button_text: string
  description: string
  disclaimer: string
  label: string
  placeholder: string
  title: string
}

interface CaptchaWidget extends HTMLElement {
  verify(): Promise<{ payload: string } | null>
  reset(): void
}

const props = defineProps({
  endpoint: {
    type: String,
    required: true,
  },
  newsletterText: {
    default: null,
  },
  enabled: {
    default: false,
  }
})
const newsletterTranslation: NewsletterTranslation = typeof props.newsletterText === 'string' ? JSON.parse(props.newsletterText) : JSON.parse(JSON.stringify(props.newsletterText))
const cookieApi = useCookies()
const errorMessage: Ref<string> = ref('')
const form: Ref<HTMLFormElement | null> = ref(null)
const formId = useId()
const captcha = ref<CaptchaWidget | null>(null)
const language = document.documentElement.lang.split(/[-_]/)[0].toLowerCase() || 'en'
const newsletterCookieKey = 'newsletter-subscribed'
const loading: Ref<boolean> = ref(false)
const subscribedToNewsletter: Ref<boolean> = ref(false)

onMounted(() => {
  subscribedToNewsletter.value = cookieApi.cookies.get(newsletterCookieKey) === '1'
})

async function submitForm($event: Event) {
  $event.preventDefault()

  if (loading.value || subscribedToNewsletter.value) {
    return
  }

  loading.value = true
  errorMessage.value = ''

  try {
    const formElement = form.value
    const widget = captcha.value
    if (formElement === null || widget === null) {
      errorMessage.value = defaultErrorMessage()
      return
    }

    if (!formElement.reportValidity()) {
      return
    }

    const verification = await widget.verify()
    if (!verification?.payload) {
      errorMessage.value = defaultErrorMessage()
      return
    }

    const serializedFormData = getSerializedFormData(formElement)
    serializedFormData.set('altcha', verification.payload)
    const endpoint = new URL(props.endpoint, window.location.origin)
    endpoint.searchParams.set('lang', language)

    const resp = await fetch(endpoint, {
      method: formElement.method,
      body: serializedFormData.toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })

    if (!resp.ok) {
      errorMessage.value = resp.status === 429 ? translate('newsletter.message.too_many_requests') : defaultErrorMessage()
      return
    }

    cookieApi.cookies.set(newsletterCookieKey, '1')
    subscribedToNewsletter.value = true
  } catch (error: unknown) {
    errorMessage.value = defaultErrorMessage()
  } finally {
    captcha.value?.reset()
    loading.value = false
  }
}

function defaultErrorMessage(): string {
  return translate('newsletter.message.error')
}

function getSerializedFormData(formElement: HTMLFormElement): URLSearchParams {
  const formData = new FormData(formElement)
  let formDataRecord: Record<string, string> = {}
  formData.forEach((value: FormDataEntryValue, key: string) => {
    formDataRecord[key] = value.toString()
  })
  return new URLSearchParams(formDataRecord)
}
</script>
